import { useCallback, useRef, useState } from 'react'
import type {
  BrandLearningPreviewResponse,
  BrandLearningSourceSummary,
  BrandLearningSummary,
} from '@mondaygpt/types'
import { previewBrandLearning } from 'modules/brand-learning/api/brand-learning-api'

export type PreviewSource = 'instagram' | 'website'
export type SourcePhase = 'pending' | 'done' | 'failed'

export interface SourceState {
  phase: SourcePhase
  response?: BrandLearningPreviewResponse
  previewedWith?: { instagramHandle?: string; websiteUrl?: string }
}

/**
 * `null` for a source means it was NOT attempted in this batch (the user didn't
 * provide that input). Distinguish from `{ phase: 'failed' }`, which means we
 * tried and the preview API errored.
 */
export interface PreviewBatchState {
  instagram: SourceState | null
  website: SourceState | null
  /** True when every attempted source has settled (done or failed). True trivially when no sources were attempted. */
  isSettled: boolean
  attemptedCount: number
  doneCount: number
}

const EMPTY: PreviewBatchState = {
  instagram: null,
  website: null,
  isSettled: true,
  attemptedCount: 0,
  doneCount: 0,
}

export interface PreviewBatchResultRow {
  source: PreviewSource
  response: BrandLearningPreviewResponse
  previewedWith: { instagramHandle?: string; websiteUrl?: string }
}

/**
 * Project the batch state down to the shape BrandCardStep already consumes —
 * a `BrandLearningSummary` (sources array with rawPerPostData stripped).
 */
export function getBatchSummary(batch: PreviewBatchState): BrandLearningSummary {
  const sources: BrandLearningSourceSummary[] = []
  for (const source of ['instagram', 'website'] as const) {
    const state = batch[source]
    if (state?.phase === 'done' && state.response) {
      const { rawPerPostData: _rawPerPostData, ...sourceSummary } = state.response
      sources.push(sourceSummary)
    }
  }
  return { sources }
}

/**
 * Project the batch state down to the `pendingBrandLearningPreviews` payload
 * persistBrandKit expects — one row per successfully-previewed source so the
 * raw analysis blob can be flushed to the just-created business after submit.
 */
export function getBatchPendingPreviews(batch: PreviewBatchState): PreviewBatchResultRow[] {
  const rows: PreviewBatchResultRow[] = []
  for (const source of ['instagram', 'website'] as const) {
    const state = batch[source]
    if (state?.phase === 'done' && state.response && state.previewedWith) {
      rows.push({ source, response: state.response, previewedWith: state.previewedWith })
    }
  }
  return rows
}

interface StartInputs {
  instagramHandle?: string
  websiteUrl?: string
}

/**
 * Fires brand-learning previews for whichever sources the user provided, in
 * parallel. Lifted out of BrandCardStep so the parent flow can wait on these
 * (alongside the website scan) before advancing to the brand card screen —
 * the card is then guaranteed to render fully populated instead of with
 * loading skeletons.
 *
 * Per-source state lets ScanningStep narrate progress ("Studying your
 * Instagram posts…") as each preview lands.
 *
 * Errors are swallowed (logged to console only) — a failed preview becomes
 * a `failed` source state, which `getBatchSummary` skips. The server-side
 * mining kickoff (runInitialMining) re-mines on persist, so dropping a
 * preview here means a small staleness, not data loss.
 */
export function useBrandLearningPreviewBatch() {
  const [state, setState] = useState<PreviewBatchState>(EMPTY)
  // Per-run token so a second start() (user backs out and re-submits) doesn't
  // splice the first run's late-arriving promises into the new batch's state.
  const runIdRef = useRef(0)

  const start = useCallback((inputs: StartInputs) => {
    const myRunId = ++runIdRef.current
    const handle = inputs.instagramHandle?.trim() || undefined
    const url = inputs.websiteUrl?.trim() || undefined

    const initial: PreviewBatchState = {
      instagram: handle ? { phase: 'pending' } : null,
      website: url ? { phase: 'pending' } : null,
      isSettled: !handle && !url,
      attemptedCount: (handle ? 1 : 0) + (url ? 1 : 0),
      doneCount: 0,
    }
    setState(initial)
    if (initial.isSettled) return

    const updateSource = (source: PreviewSource, next: SourceState) => {
      setState(curr => {
        if (myRunId !== runIdRef.current) return curr
        const merged: PreviewBatchState = { ...curr, [source]: next }
        const ig = merged.instagram
        const web = merged.website
        const igSettled = ig === null || ig.phase !== 'pending'
        const webSettled = web === null || web.phase !== 'pending'
        const doneCount = (ig?.phase === 'done' ? 1 : 0) + (web?.phase === 'done' ? 1 : 0)
        return { ...merged, isSettled: igSettled && webSettled, doneCount }
      })
    }

    if (handle) {
      const previewedWith = { instagramHandle: handle }
      previewBrandLearning({ source: 'instagram', instagramHandle: handle })
        .then(response => updateSource('instagram', { phase: 'done', response, previewedWith }))
        .catch(err => {
          console.warn('[useBrandLearningPreviewBatch] instagram preview failed:', err)
          updateSource('instagram', { phase: 'failed', previewedWith })
        })
    }
    if (url) {
      const previewedWith = { websiteUrl: url }
      previewBrandLearning({ source: 'website', websiteUrl: url })
        .then(response => updateSource('website', { phase: 'done', response, previewedWith }))
        .catch(err => {
          console.warn('[useBrandLearningPreviewBatch] website preview failed:', err)
          updateSource('website', { phase: 'failed', previewedWith })
        })
    }
  }, [])

  const reset = useCallback(() => {
    runIdRef.current++
    setState(EMPTY)
  }, [])

  return { state, start, reset }
}
